/**
 * Order display status — pure presentation-layer merge of the two order
 * status axes into ONE user-facing value, shared web + mobile.
 *
 * This is the PR #348 derive lifted out of `lib/orders/derive-display-status.ts`
 * into the contract package so the mobile mirror (DISPLAY_STATUS.md §10) can
 * import the SAME derivation + label table instead of hand-copying the unions
 * (mobile previously hand-copied `TrackingStatus` in
 * `apps/mobile/lib/api-orders.ts`). Same precedent as `item-card-state.ts`:
 * one canonical declaration, both platforms are thin adapters. Visual config
 * (icons, badge classes) stays platform-local —
 * `components/orders/_components/order-status-display.tsx` on web.
 *
 * The DB carries two INDEPENDENT axes on `seller_orders`:
 *   - `status`          — marketplace lifecycle (pending → … → delivered),
 *                         mutated only by `lib/orders/decide-order-status-transition.ts`
 *   - `tracking_status` — carrier lifecycle, written by the Shippo
 *                         `track_updated` webhook + reconciliation cron
 * Rendering both side by side ("Shipped" + "Delivered by USPS") reads as a
 * contradiction to users. {@link deriveOrderDisplayStatus} collapses them
 * (plus `label_status`) into a single {@link OrderDisplayStatus} for badge
 * rendering.
 *
 * PRESENTATION ONLY — this changes nothing on the write side:
 *   - `lib/orders/decide-order-status-transition.ts` (the status FSM) is
 *     UNTOUCHED; `carrier_DELIVERED` still never promotes `status`
 *     (operator-fixed buyer-agency invariant, HANDOFF §F1).
 *   - No schema change. Raw axes stay readable for debugging (tooltip +
 *     admin dual-display).
 *   - CTA gating (confirm-delivery, mark-shipped, …) MUST keep keying off the
 *     raw `status`, never off the value returned here.
 *
 * Pure + total: every (status × trackingStatus × labelStatus) triple maps to
 * exactly one display value; both unions carry `never`-probes so extending
 * either one fails `tsc` here first.
 *
 * @see .tondocs/orders/DISPLAY_STATUS.md — spec (§3 union, §4 matrix, §5 i18n, §10 mobile mirror)
 * @see lib/orders/decide-order-status-transition.ts — the WRITE-side FSM
 * @see lib/shippo/select-order-row.ts — `TrackingStatus` narrowing boundary (web)
 * @see components/orders/_components/order-status-display.tsx — web visual config
 */

import { SELLER_ORDER_STATUSES, type SellerOrderStatus } from "./orders-update-status";

/** File-local exhaustiveness probe — same convention as `item-card-state.ts`. */
function unreachable(x: never): never {
  throw new Error(`order-display-status: unreachable variant ${JSON.stringify(x)}`);
}

// ---------------------------------------------------------------------------
// Carrier axis — TrackingStatus
// ---------------------------------------------------------------------------

/**
 * Lowercase DB-side carrier tracking states, mirror of
 * `SHIPPO_TRACK_WEBHOOK_STATUSES` (uppercase wire values) after the
 * `TRACK_STATUS_MAP` lowercase mapping in `lib/shippo/webhook-transition.ts`.
 * `UNKNOWN` maps to `null` (no row write), so it is not part of this union.
 *
 * Independent axis from `seller_orders.status` (the marketplace lifecycle:
 * pending → shipped → delivered). Carrier scan = `tracking_status='delivered'`
 * does NOT imply buyer-confirmed `status='delivered'` — see the two-axis
 * invariant pinned in
 * `tests/integration/shippo-webhook-track-updated.test.ts`.
 */
export const TRACKING_STATUSES = [
  "pre_transit",
  "transit",
  "delivered",
  "returned",
  "failure",
] as const;

export type TrackingStatus = (typeof TRACKING_STATUSES)[number];

// ---------------------------------------------------------------------------
// Marketplace-axis boundary parse
// ---------------------------------------------------------------------------

const SELLER_ORDER_STATUS_SET: ReadonlySet<string> = new Set(SELLER_ORDER_STATUSES);

/**
 * Total parse of an untyped status string into the contract union — the
 * boundary every badge crosses, because the row shapes the order components
 * receive still type `status` as a bare `string`.
 *
 * Returns `null` (not a fallback) for anything outside the union so the CALLER
 * picks the degradation explicitly. Legacy display aliases (`received`,
 * `sent`, `completed`, `in_progress`) are NOT handled here — run the value
 * through `normalizeSellerOrderStatusAlias` (`./seller-order-receipt`, exposed
 * on web as `normalizeStatus` in `lib/orders/normalize-status.ts`) first; that
 * alias map is shared by every order surface on both platforms and predates
 * this module. {@link toSellerOrderStatus} is the two-step version for callers
 * that want alias-collapse AND the union in one call.
 *
 * @see .tondocs/skills/type-driven-design.md §5 (Parse Don't Validate)
 */
export function parseSellerOrderStatus(
  raw: string | null | undefined,
): SellerOrderStatus | null {
  if (raw == null) return null;
  const candidate = raw.toLowerCase();
  return SELLER_ORDER_STATUS_SET.has(candidate)
    ? (candidate as SellerOrderStatus)
    : null;
}

// ---------------------------------------------------------------------------
// Merged display union
// ---------------------------------------------------------------------------

/**
 * Canonical merged display union (DISPLAY_STATUS.md §3). Eleven values —
 * the six marketplace statuses minus `pending/processing` ambiguity, plus the
 * carrier-derived refinements.
 *
 * Deliberately NO `out_for_delivery`: only the DB CHECK constraint mentions it.
 * The contract enum (`SHIPPO_TRACK_WEBHOOK_STATUSES`), `TRACK_STATUS_MAP` and
 * both TS unions omit it — Shippo does not emit it as a top-level track state.
 * Dead headroom, not a state.
 */
export const ORDER_DISPLAY_STATUSES = [
  "pending",
  "processing",
  "label_ready",
  "shipped",
  "in_transit",
  "arrived",
  "delivered",
  "returned",
  "delivery_issue",
  "cancelled",
  "refunded",
] as const;

export type OrderDisplayStatus = (typeof ORDER_DISPLAY_STATUSES)[number];

export interface DeriveOrderDisplayStatusInput {
  /** Marketplace axis — `seller_orders.status`. Parsed/enumerated by
   * `SELLER_ORDER_STATUSES` (`orders-update-status.ts`). */
  status: SellerOrderStatus;
  /**
   * Carrier axis — `seller_orders.tracking_status`, ALREADY narrowed by the
   * platform boundary (`projectLabelStateForUi` in
   * `lib/shippo/select-order-row.ts` on web). Legacy / unknown DB strings
   * land here as `null`, so this function does not re-validate
   * (parse-don't-validate: the boundary already did it).
   */
  trackingStatus: TrackingStatus | null;
  /**
   * Label lifecycle — `seller_orders.label_status`. Typed `string | null`
   * (not `LabelStatus`) because the column is a plain varchar and the UI
   * projection surfaces it raw; only the exact value `'purchased'` carries
   * meaning here, everything else falls through to the base status.
   */
  labelStatus: string | null;
}

/** The one `label_status` value that upgrades pending/processing → label_ready. */
const LABEL_PURCHASED = "purchased";

/**
 * Merge the two axes into a single display value. Precedence
 * (DISPLAY_STATUS.md §4, in order):
 *
 *  1. Marketplace terminal wins — `cancelled` / `refunded` / `delivered`.
 *     Buyer-confirmed `delivered` beats any carrier signal.
 *  2. Carrier exceptional wins next — `failure` → `delivery_issue`,
 *     `returned` → `returned`. Mirrors the write FSM, where both
 *     email-alert without flipping `status`.
 *  3. Carrier progress refines the active statuses —
 *     `delivered` → `arrived`, `transit` → `in_transit`.
 *     (`transit` on `pending` is reachable only when a TRANSIT auto-promote
 *     guard blocked — preorder / label drift. Showing real carrier movement
 *     is honest; CTA logic is unaffected because it keys off raw `status`.)
 *  4. Base — `shipped` (covers `pre_transit`/null tracking),
 *     `pending|processing` + `label_status='purchased'` → `label_ready`,
 *     else the raw status.
 */
export function deriveOrderDisplayStatus(
  input: DeriveOrderDisplayStatusInput,
): OrderDisplayStatus {
  const { status, trackingStatus, labelStatus } = input;

  // --- 1. Marketplace terminal wins -----------------------------------------
  // All six members are cased so `default` narrows `status` to `never`;
  // adding a seventh `SellerOrderStatus` fails tsc right here.
  switch (status) {
    case "cancelled":
      return "cancelled";
    case "refunded":
      return "refunded";
    case "delivered":
      return "delivered";
    case "pending":
    case "processing":
    case "shipped":
      break;
    default:
      return unreachable(status);
  }
  // `status` is now narrowed to "pending" | "processing" | "shipped".

  // --- 2 + 3. Carrier axis refines the active statuses ----------------------
  if (trackingStatus !== null) {
    switch (trackingStatus) {
      case "failure":
        return "delivery_issue";
      case "returned":
        return "returned";
      case "delivered":
        return "arrived";
      case "transit":
        return "in_transit";
      case "pre_transit":
        // Carries no more information than the base status (a label exists,
        // which `label_status` already tells us) — fall through to step 4.
        break;
      default:
        return unreachable(trackingStatus);
    }
  }

  // --- 4. Base --------------------------------------------------------------
  switch (status) {
    case "shipped":
      return "shipped";
    case "pending":
    case "processing":
      return labelStatus === LABEL_PURCHASED ? "label_ready" : status;
    default:
      return unreachable(status);
  }
}

// ---------------------------------------------------------------------------
// Label keys (i18n) + perspective — DISPLAY_STATUS.md §5
// ---------------------------------------------------------------------------

/**
 * Who is looking at the badge. Only `arrived` renders differently per
 * perspective (buyer must act, seller is done) — see {@link DisplayStatusLabel}.
 */
export type OrderStatusPerspective = "buyer" | "seller";

/**
 * Label descriptor. A discriminated union rather than a bare `i18nKey: string`
 * so the ONE perspective-split status cannot silently ship the buyer's
 * call-to-action wording to a seller: there is no single key to pick wrongly.
 * Resolve with {@link resolveDisplayStatusLabelKey}; consumers should not
 * branch on `kind` themselves.
 *
 * Keys are relative to the `orders.displayStatus` i18n namespace (the same
 * namespace on both platforms).
 *
 * @see .tondocs/skills/type-driven-design.md §2 (Make Illegal States Unrepresentable)
 */
export type DisplayStatusLabel =
  | { kind: "fixed"; i18nKey: string }
  | { kind: "perspective"; buyerI18nKey: string; sellerI18nKey: string };

const fixed = (i18nKey: string): DisplayStatusLabel => ({ kind: "fixed", i18nKey });

/**
 * THE canonical label-key table — one entry per {@link OrderDisplayStatus}.
 * `Record<OrderDisplayStatus, …>` means extending the union fails `tsc` here
 * until a label is chosen for it. Platform visual config (icon + badge
 * classes) stays local to each platform, keyed off the same union.
 */
export const ORDER_DISPLAY_STATUS_LABELS: Record<
  OrderDisplayStatus,
  DisplayStatusLabel
> = {
  pending: fixed("pending"),
  processing: fixed("processing"),
  label_ready: fixed("labelReady"),
  shipped: fixed("shipped"),
  in_transit: fixed("inTransit"),
  // Carrier scanned "delivered" but the buyer has NOT confirmed. The one
  // perspective-split cell: buyer sees a call to action, seller sees a fact.
  arrived: {
    kind: "perspective",
    buyerI18nKey: "arrivedBuyer",
    sellerI18nKey: "arrivedSeller",
  },
  delivered: fixed("delivered"),
  returned: fixed("returned"),
  delivery_issue: fixed("deliveryIssue"),
  cancelled: fixed("cancelled"),
  refunded: fixed("refunded"),
};

/**
 * Resolve the i18n key (relative to the `orders.displayStatus` namespace) for
 * a display status as seen from `perspective`.
 *
 * ```ts
 * const t = useTranslations("orders.displayStatus");
 * const key = resolveDisplayStatusLabelKey(displayStatus, "buyer");
 * t(key);
 * ```
 */
export function resolveDisplayStatusLabelKey(
  status: OrderDisplayStatus,
  perspective: OrderStatusPerspective,
): string {
  const label = ORDER_DISPLAY_STATUS_LABELS[status];
  switch (label.kind) {
    case "fixed":
      return label.i18nKey;
    case "perspective":
      switch (perspective) {
        case "buyer":
          return label.buyerI18nKey;
        case "seller":
          return label.sellerI18nKey;
        default:
          return unreachable(perspective);
      }
    default:
      return unreachable(label);
  }
}
